import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { globalpermissions } from '../../../../Services/Permission.js';
import {
  hasBfViewCapability,
  hasResourceScopeSelection,
  setBusinessFunctionsModuleAccess,
  syncBusinessFunctionsObjectPolicies,
} from '../../utils/businessFunctionsPermissionUtils';
import useProfileSx from '../../hooks/useProfileSx';
import PermissionNodeToggle from './PermissionNodeToggle';
import BusinessFunctionsScopeTree from './BusinessFunctionsScopeTree';

const HEADER_GRID = 'minmax(220px, 1fr) 92px';
const BUSINESS_SERVICE_MAPPING_PERMISSION =
  globalpermissions?.leadDashboard?.mapBusinessServices;

export default function BusinessFunctionsPermissionSection({
  section,
  grants,
  resourceTree,
  isResourceTreeLoading,
  disabled = false,
  onGrantsPatch,
}) {
  const sx = useProfileSx();
  const [expanded, setExpanded] = useState(true);
  const moduleViewEnabled = hasBfViewCapability(grants);
  const hasScopeSelection = hasResourceScopeSelection(grants);
  const canMapBusinessServices = Boolean(
    grants.features?.[BUSINESS_SERVICE_MAPPING_PERMISSION]
  );

  const scopeAccounts = useMemo(() => resourceTree?.accounts || [], [resourceTree?.accounts]);

  const handleModuleViewChange = (granted) => {
    onGrantsPatch?.(setBusinessFunctionsModuleAccess(grants, granted));
    if (granted) setExpanded(true);
  };

  const handleMappingPermissionChange = (event) => {
    onGrantsPatch?.({
      ...grants,
      features: {
        ...grants.features,
        [BUSINESS_SERVICE_MAPPING_PERMISSION]: event.target.checked,
      },
    });
  };

  return (
    <Box sx={sx.permissionModuleSection}>
      <Box sx={{ ...sx.permissionModuleHeaderRow, gridTemplateColumns: HEADER_GRID }}>
        <Box sx={sx.permissionModuleTitleCell}>
          <IconButton
            size="small"
            aria-label={expanded ? 'Collapse module' : 'Expand module'}
            onClick={() => setExpanded((prev) => !prev)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" sx={sx.permissionModuleTitle}>
              {section?.label || 'Business Functions'}
            </Typography>
            {section?.description ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {section.description}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Box sx={sx.permissionModuleViewCell}>
          <PermissionNodeToggle
            checked={moduleViewEnabled}
            indeterminate={false}
            disabled={disabled}
            onChange={handleModuleViewChange}
          />
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 2.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Enable <strong>View</strong> to allow access to business functions. Select functions,
            services, and optionally fine-tune <strong>form fields</strong> below — nothing is
            accessible until explicitly granted.
          </Typography>
        </Box>

        {!moduleViewEnabled ? (
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              Turn on <strong>View</strong> to configure which business functions and services this
              profile can access.
            </Alert>
          </Box>
        ) : null}

        {moduleViewEnabled && !hasScopeSelection ? (
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Alert severity="warning" sx={{ py: 0.5 }}>
              No business functions or services are selected. This profile cannot access any
              until you grant <strong>View</strong> on specific items below.
            </Alert>
          </Box>
        ) : null}

        <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={canMapBusinessServices}
                disabled={disabled}
                onChange={handleMappingPermissionChange}
              />
            }
            label="Map business services"
          />
          <FormHelperText sx={{ ml: 4 }}>
            Allows this profile to view and manage mappings for business services it can edit.
          </FormHelperText>
        </Box>

        {moduleViewEnabled ? (
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
            <Typography variant="subtitle2" sx={{ px: 2.5, pb: 1 }}>
              Business functions &amp; services
            </Typography>
            <BusinessFunctionsScopeTree
              tree={resourceTree}
              resources={grants.resources}
              grants={grants}
              isLoading={isResourceTreeLoading}
              disabled={disabled}
              onChange={(resources) =>
                onGrantsPatch?.(
                  syncBusinessFunctionsObjectPolicies({
                    ...grants,
                    resources,
                  })
                )
              }
              onGrantsChange={(nextGrants) => onGrantsPatch?.(nextGrants)}
            />
          </Box>
        ) : null}

        {moduleViewEnabled && !isResourceTreeLoading && scopeAccounts.length === 0 ? (
          <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              No business functions exist for this organisation yet. Platform administrators create
              them from Management — they will appear here for scoped access.
            </Typography>
          </Box>
        ) : null}
      </Collapse>
    </Box>
  );
}
